import CTNCard from '@ctn/ui/components/ctn-card';
import { type CTNButtonProps } from '@ctn/ui/types/ctn-button';
import { type Icon } from '@ctn/ui/types/generic';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CardIconTemplate } from '@/components/CardsTemplate';
import CardChartDoughnutTemplate from '@/components/CardsTemplate/CardChartDoughnutTemplate';
import CardChartStackedTemplate from '@/components/CardsTemplate/CardChartStackedTemplate';
import PageHeader from '@/components/PageHeader';
import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import useApiData from '@/hooks/useApiData';
import useRouteTramite from '@/hooks/useRouteTramite';
import {
	type EstadisticaResponse,
	postSubsanacionesEstadisticas,
	postTramitesEstadisticas,
} from '@/services/tramite.service';

import { ESTADO_SUBSANACION, ESTADOS_OPERACION, TIPO_SUBSANACION } from '@/constants/tramite.constants';
import { getDoughnutData, getStackedBarList } from '@/helpers/charts';

const TramiteHome = () => {
	const ROUTES_TRAMITE = useRouteTramite();
	const navigate = useNavigate();
	const [estadisticasSubanaciones, setEstadisticasSubanaciones] = useState<EstadisticaResponse>();
	const [estadisticasTramite, setEstadisticasTramite] = useState<EstadisticaResponse>();
	const { triggerFetch } = useApiData({ loading: ['estadisticasTramite', 'estadisticasSubsanar'] as const });
	const { setBreadcrumb, updateMessages } = usePageContext();
	const { getTramiteData } = useGlobalContext();

	const CARDS_CONTENT: Array<{ id: number; icon: Icon; title: string; url: string }> = [
		{ id: 1, icon: 'list', title: 'Solicitudes / Borradores', url: ROUTES_TRAMITE.SOLICITUDES },		
		{ id: 2, icon: 'warningOutline', title: 'Errores subsanables', url: ROUTES_TRAMITE.SUBSANACIONES },
		{ id: 3, icon: 'documents', title: 'Solicitudes documentación', url: ROUTES_TRAMITE.SOLICITUDES_DOCUMENTACION },
	];

	const actions: CTNButtonProps[] = [
		{ label: 'Nueva solicitud', mode: 'primary', onClick() {
			navigate(ROUTES_TRAMITE.BORRADORES_NEW);
		}},
	];

	const getEstadisticasTramite = async () => {
		const { data } = await triggerFetch({
			fetch: postTramitesEstadisticas,
			name: 'estadisticasTramite',
			error: 'Ha ocurrido un error al obtener los datos de la estadística',
		});
		if (data) {
			setEstadisticasTramite(data);
		}
	};

	const getEstadisticasSubsanaciones = async () => {
		const { data } = await triggerFetch({
			fetch: postSubsanacionesEstadisticas,
			name: 'estadisticasSubsanar',
			error: 'Ha ocurrido un error al obtener los datos de la estadística',
		});
		if (data) {
			setEstadisticasSubanaciones(data);
		}
	};

	useEffect(() => {
		console.log('Initializing TramiteHome, setting breadcrumb and fetching statistics');
		setBreadcrumb([{ label: getTramiteData().tipoTramite.descripcion, url: location.pathname }]);
		getEstadisticasSubsanaciones();
		getEstadisticasTramite();
		updateMessages();
	}, []);

	const colorMapEstadoSolicitudIds: Record<number, string> = {
		[ESTADOS_OPERACION.PENDIENTE]: '#EF9605',
		[ESTADOS_OPERACION.EN_CURSO]: '#1975D2',
		[ESTADOS_OPERACION.FIRMADA]: '#00997F',
		[ESTADOS_OPERACION.FINALIZADA]: '#318311',
		[ESTADOS_OPERACION.CANCELADA_EF]: '#263240',
		[ESTADOS_OPERACION.CANCELADA_NOT]: '#263240',
		[ESTADOS_OPERACION.CANCELADA_VENCIMIENTO]: '#263240',
	};

	const colorMapSubsanacionesIds: Record<number, string> = {
		[ESTADO_SUBSANACION.PENDIENTE]: '#EF9605',
		[ESTADO_SUBSANACION.SUBSANADA]: '#318311',
	};

	const colorMapSubsanacionesTipoIds: Record<number, string> = {
		[TIPO_SUBSANACION.SUJETO]: '#00997F',
		[TIPO_SUBSANACION.DOCUMENTO]: '#EF9605',
	};

	const solicitudesEstado = useMemo(
		() =>
			estadisticasTramite
				? {
					total: estadisticasTramite.total,
					data: getDoughnutData({
						data: estadisticasTramite,
						colorMap: colorMapEstadoSolicitudIds,
						groupBy: 'estado',
					}),
				}
				: undefined,
		[estadisticasTramite],
	);

	const solicitudesStacked = useMemo(
		() =>
			estadisticasTramite
				? {
					total: estadisticasTramite.total,
					data: getStackedBarList({
						data: estadisticasTramite,
						colorMap: colorMapEstadoSolicitudIds,
						groupBy: 'tipo',
					}),
				}
				: undefined,
		[estadisticasTramite],
	);

	const subsanacionesTipo = useMemo(
		() =>
			estadisticasSubanaciones
				? {
					total: estadisticasSubanaciones.total,
					data: getDoughnutData({
						data: estadisticasSubanaciones,
						colorMap: colorMapSubsanacionesTipoIds,
						groupBy: 'tipo',
					}),
				}
				: undefined,
		[estadisticasSubanaciones],
	);

	const subsanacionesEstado = useMemo(
		() =>
			estadisticasSubanaciones
				? {
					total: estadisticasSubanaciones.total,
					data: getDoughnutData({
						data: estadisticasSubanaciones,
						colorMap: colorMapSubsanacionesIds,
						groupBy: 'estado',
					}),
				}
				: undefined,
		[estadisticasSubanaciones],
	);

	const subsanacionesStacked = useMemo(
		() =>
			estadisticasSubanaciones
				? {
					total: estadisticasSubanaciones.total,
					data: getStackedBarList({
						data: estadisticasSubanaciones,
						colorMap: colorMapSubsanacionesIds,
						groupBy: 'tipo',
					}),
				}
				: undefined,
		[estadisticasSubanaciones],
	);

	return (
		<div className="p-sub-home">
			<PageHeader hasHelp title={getTramiteData().tipoTramite.descripcion} actions={actions} className="u-mt-8" />
			<ul className="[ o-auto-grid o-list ] [ u-mt-24 ]">
				{CARDS_CONTENT.map(({ id, icon, title, url }) => (
					<li key={id} className="o-list__item">
						<CTNCard aspectRatio className="c-pnp-card c-pnp-card--procedure" onClick={() => navigate(url)}>
							<CardIconTemplate iconColor="blue-50" icon={icon} title={title} />
						</CTNCard>
					</li>
				))}
			</ul>
			<section className="[ p-sub-home__charts ] [ u-mt-32 ]">
				<h3 className="c-pnp-heading-h3">Resumen del estado de las solicitudes</h3>
				<ul className="[ o-auto-grid o-fade-in o-list ] [ u-mt-24 ]">
					<li className="o-list__item">
						<CTNCard aspectRatio>
							<CardChartDoughnutTemplate
								classNameChart={classNames({
									['c-pnp-chart c-pnp-chart--disabled']: !solicitudesEstado?.data || solicitudesEstado?.total === 0,
								})}
								title="Solicitudes por estado"
								data={solicitudesEstado && solicitudesEstado?.total > 0 ? solicitudesEstado?.data : undefined}
							/>
						</CTNCard>
					</li>
					{solicitudesStacked?.total && solicitudesStacked?.total > 0 ? (
						<li className="o-list__item_lg">
							<CTNCard aspectRatio={false} className="c-pnp-card c-pnp-card--stacked">
								<CardChartStackedTemplate
									title="Solicitudes por estado"
									className="o-fade-in"
									data={solicitudesStacked?.data}
									total={solicitudesStacked?.total}
								/>
							</CTNCard>
						</li>
					) : <></>}
				</ul>
			</section>
			<section className="[ p-sub-home__charts ] [ u-mt-32 ]">
				<h3 className="c-pnp-heading-h3">Resumen del estado de las subsanaciones</h3>
				<ul className="[ o-auto-grid o-fade-in o-list ] [ u-mt-24 ]">
					<li className="o-list__item">
						<CTNCard aspectRatio>
							<CardChartDoughnutTemplate
								classNameChart={classNames({
									['c-pnp-chart c-pnp-chart--disabled']: !subsanacionesTipo?.data || subsanacionesTipo?.total === 0,
								})}
								title="Subsanaciones por tipo"
								data={subsanacionesTipo && subsanacionesTipo?.total > 0 ? subsanacionesTipo?.data : undefined}
							/>
						</CTNCard>
					</li>
					<li className="o-list__item">
						<CTNCard aspectRatio>
							<CardChartDoughnutTemplate
								classNameChart={classNames({
									['c-pnp-chart c-pnp-chart--disabled']: !subsanacionesEstado?.data || subsanacionesEstado?.total === 0,
								})}
								title="Subsanaciones por estado"
								data={subsanacionesEstado && subsanacionesEstado?.total > 0 ? subsanacionesEstado?.data : undefined}
							/>
						</CTNCard>
					</li>
					{subsanacionesStacked?.total && subsanacionesStacked?.total > 0 ? (
						<li className="o-list__item">
							<CTNCard aspectRatio={false} className="c-pnp-card c-pnp-card--stacked">
								<CardChartStackedTemplate
									title="Subsanaciones por tipo y estado"
									className="o-fade-in"
									data={subsanacionesStacked?.data}
									total={subsanacionesStacked?.total}
								/>
							</CTNCard>
						</li>
					) : <></>}
				</ul>
			</section>
		</div>
	);
};

export default TramiteHome;
